// Edge Diagnostics page (owner request 2026-10-14): is the current rule set profitable after real friction and real latency? READ-ONLY with respect to trading.
// Binds to GET /api/edge/diagnostics (shared/edge_diagnostics.py): taken vs blocked, gate impact, latency and friction, parameter health, one verdict line.
// A failed section shows its error; a failed or old fetch shows STALE instead of the previous verdict as if fresh. Logic helpers: lib/edgeDiagnostics.js (tested).
import React, { useState, useEffect, useCallback } from 'react'
import { Gauge, RefreshCw, AlertTriangle } from 'lucide-react'
import { edgeDiagnostics } from '../lib/api'
import { HORIZONS, DAYS, fmtPct, fmtShare, fmtSec, fmtInterval, gateStyle, signTone, failedSections, gatesSentence, isStale, coverageRows } from '../lib/edgeDiagnostics'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const CARD = { background: '#111827', border: '1px solid #1f2937', borderRadius: '0.75rem' }
const POLL_MS = 60000

function Section({ title, sub, children }) {
  return (
    <section className="p-4 space-y-3" style={CARD}>
      <div>
        <h3 className="text-slate-200 font-semibold text-sm tracking-widest" style={{ fontFamily: 'Rajdhani, sans-serif' }}>{title}</h3>
        {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
      </div>
      {children}
    </section>
  )
}

function Kv({ label, value, tone }) {
  return (
    <div className="rounded-lg p-3" style={{ background: '#0b1220', border: '1px solid #1f2937' }}>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-sm mt-1 ${tone || 'text-slate-200'}`} style={MONO}>{value}</p>
    </div>
  )
}

function SectionError({ s }) {
  if (!s || !s.error) return null
  return <p className="text-xs text-rose-300" style={MONO}>This section failed: {s.error}</p>
}

function TakenVsBlocked({ s, horizon }) {
  if (!s || s.error) return <SectionError s={s} />
  const f = s.forward || {}
  const tk = f.taken || {}, bl = f.blocked || {}
  const ex = s.exits || {}
  const sentence = gatesSentence(s)
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kv label={`Signals (${s.engine})`} value={(s.n_total ?? 0).toLocaleString()} />
        <Kv label="Taken" value={`${(s.n_taken ?? 0).toLocaleString()} (${s.taken_pct ?? 'n/a'}%)`} />
        <Kv label="Blocked" value={`${(s.n_blocked ?? 0).toLocaleString()} (${s.blocked_pct ?? 'n/a'}%)`} />
        <Kv label={s.net_comparable ? 'Net effect of the gates' : 'Net effect of the gates (withheld)'} value={fmtPct(s.net_effect_of_gates_pct)} tone={signTone(s.net_effect_of_gates_pct)} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={MONO}>
          <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">{horizon} forward</th><th className="pr-3">Net of spread</th><th className="pr-3">Gross</th><th className="pr-3">1-bar delayed</th><th>Rows (with spread)</th></tr></thead>
          <tbody>
            {[['Taken (filled)', tk], ['Blocked (hypothetical)', bl]].map(([name, g]) => (
              <tr key={name} className="border-t border-slate-800">
                <td className="py-1 pr-3 text-slate-300">{name}</td>
                <td className={`pr-3 ${signTone(g.net && g.net.mean)}`}>{fmtPct(g.net && g.net.mean)} <span className="text-slate-600">{fmtInterval(g.net)}</span></td>
                <td className={`pr-3 ${signTone(g.gross && g.gross.mean)}`}>{fmtPct(g.gross && g.gross.mean)}</td>
                <td className={`pr-3 ${signTone(g.gross_delayed && g.gross_delayed.mean)}`}>{fmtPct(g.gross_delayed && g.gross_delayed.mean)}</td>
                <td className="text-slate-400">{(g.rows ?? 0).toLocaleString()} ({(g.rows_with_spread ?? 0).toLocaleString()})</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sentence && <p className="text-xs text-slate-300">{sentence}</p>}
      {s.comparison_note && <p className="text-[11px] text-amber-300/80">{s.comparison_note}. Gross taken minus blocked: {fmtPct(s.gross_effect_of_gates_pct)}. Coverage of rows with a spread: taken {fmtShare(tk.spread_coverage, 0)}, blocked {fmtShare(bl.spread_coverage, 0)}.</p>}
      <div className="rounded-lg p-3" style={{ background: '#0b1220', border: '1px solid #1f2937' }}>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Realized Tier 2 exits (real paper fills, analyzable only)</p>
        <p className="text-xs text-slate-200 mt-1" style={MONO}>
          n={ex.n_analyzable ?? 0} of {ex.n_rows ?? 0} rows (excluded {fmtShare(ex.excluded_share)}
          {ex.fallback_included ? ', FALLBACK_NO_PARAMS included' : `, ${ex.n_fallback_excluded ?? 0} FALLBACK_NO_PARAMS left out`}) · mean {fmtPct(ex.mean_pct)} · win rate {ex.win_rate_pct ?? 'n/a'}% · sum {fmtPct(ex.sum_pct, 1)}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-400" style={MONO}>
          {Object.entries(ex.by_reason || {}).map(([r, b]) => (
            <span key={r}>{r} n={b.n} <span className={signTone(b.mean_pct)}>{fmtPct(b.mean_pct, 2)}</span> win {b.win_pct}%</span>
          ))}
        </div>
      </div>
    </div>
  )
}

function GateImpact({ s }) {
  if (!s || s.error) return <SectionError s={s} />
  if (!(s.gates || []).length) return <p className="text-xs text-slate-500">No blocked signals in the window.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" style={MONO}>
        <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">Gate</th><th className="pr-3">Blocked</th><th className="pr-3">With forward</th><th className="pr-3">Net forward of blocked</th><th className="pr-3">Gross</th><th>Verdict</th></tr></thead>
        <tbody>
          {s.gates.map(g => {
            const st = gateStyle(g.verdict)
            return (
              <tr key={g.block_code} className="border-t border-slate-800" title={st.hint}>
                <td className="py-1 pr-3 text-slate-200">{g.block_code}{g.kind === 'execution' && <span className="ml-1 text-[10px] text-slate-500">(execution, not a gate)</span>}</td>
                <td className="pr-3 text-slate-400">{g.blocked_rows.toLocaleString()}</td>
                <td className="pr-3 text-slate-400">{g.rows_with_forward.toLocaleString()} ({g.rows_with_net.toLocaleString()} with spread)</td>
                <td className={`pr-3 ${signTone(g.net && g.net.mean)}`}>{fmtPct(g.net && g.net.mean)} <span className="text-slate-600">{fmtInterval(g.net)}</span></td>
                <td className={`pr-3 ${signTone(g.gross && g.gross.mean)}`}>{fmtPct(g.gross && g.gross.mean)}</td>
                <td><span className={`text-[10px] px-1.5 py-0.5 rounded border ${st.cls}`}>{st.label}</span>{g.basis === 'gross' && <span className="ml-1 text-[10px] text-slate-500">on gross</span>}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-500 mt-2">HELPING = the signals this gate blocked lost money after cost. HURTING = they made money. Needs at least {s.min_clusters} symbol-days; intervals are over symbol-days, not rows.</p>
    </div>
  )
}

function Pctl({ label, p }) {
  return <Kv label={label} value={p && p.n ? `p50 ${fmtSec(p.p50)} · p90 ${fmtSec(p.p90)} · n=${p.n}` : 'n/a (no rows with this stamp yet)'} />
}

function LatencyFriction({ s }) {
  if (!s || s.error) return <SectionError s={s} />
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Pctl label="Decision to submit" p={s.decision_to_submit_s} />
        <Pctl label="Wait on leg 1 before the reprice leg filled" p={s.reprice_wait_s} />
        <Pctl label="Submit to broker ack" p={s.submit_to_ack_s} />
        <Pctl label="Ack to fill" p={s.ack_to_fill_s} />
        <Pctl label="Total decision to fill" p={s.total_s} />
        <Kv label="Decision-time spread (median)" value={s.decision_spread_pct && s.decision_spread_pct.p50 != null ? `${s.decision_spread_pct.p50}% · n=${s.decision_spread_pct.n}` : 'n/a'} />
        <Kv label="Fill vs decision mid (median)" value={s.fill_vs_decision_mid_pct && s.fill_vs_decision_mid_pct.p50 != null ? `${fmtPct(s.fill_vs_decision_mid_pct.p50)} · n=${s.fill_vs_decision_mid_pct.n}` : 'n/a'} />
        <Kv label="Entry friction vs average SIGNAL win" value={`${s.entry_friction_pct == null ? 'n/a' : `${s.entry_friction_pct}%`} vs ${s.edge_ref_pct == null ? 'n/a' : `${s.edge_ref_pct}%`}`} />
        <Kv label="Entries that never filled" value={`${s.unfilled} of ${(s.filled || 0) + (s.unfilled || 0)} (${fmtShare(s.unfilled_share)})`} />
      </div>
      {(s.flags || []).map(f => (
        <div key={f} className="flex items-start gap-2 rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.4)' }}>
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" /><p className="text-xs text-amber-200">{f}</p>
        </div>
      ))}
      <p className="text-[11px] text-slate-500">{s.note}</p>
    </div>
  )
}

function ParamHealth({ s }) {
  if (!s || s.error) return <SectionError s={s} />
  const cov = coverageRows(s)
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kv label="Symbols with valid params (24 h)" value={s.valid_params ?? 'n/a'} />
        <Kv label="Open Tier 2 positions" value={s.open_positions ?? 'n/a'} />
        <Kv label="Held from a prior day (EOD exits off)" value={s.held_from_prior_day ?? 'n/a'} />
        <Kv label="Newest fit" value={String(s.params_newest || 'n/a').slice(0, 19)} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={MONO}>
          <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">ET day</th><th className="pr-3">Fit runs</th><th className="pr-3">Avg fitted</th><th className="pr-3">Min-max</th><th className="pr-3">Outcome rows</th><th className="pr-3">param_version</th><th className="pr-3">decision quote</th><th>Symbols</th></tr></thead>
          <tbody>
            {(s.fit_trend || []).map(t => {
              const c = cov.find(x => x.d === t.d) || {}
              return (
                <tr key={t.d} className="border-t border-slate-800 text-slate-300">
                  <td className="py-1 pr-3">{t.d}</td><td className="pr-3">{t.runs}</td><td className="pr-3">{t.avg_fitted ?? 'n/a'}</td><td className="pr-3">{t.min_fitted}-{t.max_fitted}</td>
                  <td className="pr-3">{c.rows ?? 'n/a'}</td><td className="pr-3">{c.pv ?? 'n/a'}</td><td className="pr-3">{c.dq ?? 'n/a'}</td><td>{c.symbols ?? 'n/a'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function EdgeDiagnostics() {
  const [days, setDays] = useState(5)
  const [engine, setEngine] = useState('TIER2')
  const [horizon, setHorizon] = useState('15m')
  const [fallback, setFallback] = useState(false)
  const [resp, setResp] = useState(null)
  const [err, setErr] = useState(null)
  const [fetchedAt, setFetchedAt] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await edgeDiagnostics.get({ days, engine, horizon, includeFallback: fallback })
      if (!r || !r.success) throw new Error((r && r.error) || 'request failed')
      setResp(r)
      setErr(null)
      setFetchedAt(Date.now())
    } catch (e) {
      setErr(String((e && e.message) || e))   // the last good payload stays on screen but is marked STALE below
    } finally {
      setLoading(false)
    }
  }, [days, engine, horizon, fallback])

  useEffect(() => {
    load()
    const id = setInterval(() => { if (!document.hidden) { load(); setNow(Date.now()) } }, POLL_MS)
    return () => clearInterval(id)
  }, [load])
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 10000); return () => clearInterval(id) }, [])

  const stale = isStale(fetchedAt, err, now)
  const failed = failedSections(resp)
  const sec = (resp && resp.sections) || {}
  const sel = 'text-xs rounded px-2 py-1 bg-slate-900 border border-slate-700 text-slate-200'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-slate-200 font-semibold text-sm tracking-widest flex items-center gap-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          <Gauge className="w-4 h-4 text-cyan-400" /> EDGE DIAGNOSTICS
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <select className={sel} value={days} onChange={e => setDays(Number(e.target.value))} aria-label="window">{DAYS.map(d => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}</select>
          <select className={sel} value={engine} onChange={e => setEngine(e.target.value)} aria-label="engine"><option value="TIER2">Tier 2</option><option value="MAIN">MAIN</option></select>
          <select className={sel} value={horizon} onChange={e => setHorizon(e.target.value)} aria-label="horizon">{HORIZONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <label className="text-xs text-slate-400 flex items-center gap-1"><input type="checkbox" checked={fallback} onChange={e => setFallback(e.target.checked)} /> include FALLBACK_NO_PARAMS</label>
          <button onClick={load} className="text-slate-400 hover:text-slate-200" aria-label="refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      <div className="p-3 sticky top-0 z-10" style={{ ...CARD, borderColor: stale ? '#b45309' : '#1f2937' }} data-testid="edge-verdict">
        <p className="text-sm text-slate-100" style={MONO}>{resp ? resp.verdict : (err ? 'No verdict: the diagnostics endpoint did not answer.' : 'Loading...')}</p>
        {stale && <p className="text-[11px] text-amber-400 mt-1">STALE: {err ? `last request failed (${err})` : 'no fresh answer for over 3 minutes'}; the figures above may be out of date.</p>}
        {resp && <p className="text-[11px] text-slate-500 mt-1">Window since {String(resp.window.since).slice(0, 16)} UTC · paper results, not decision-grade while the data-health gate has RED checks or n is small.</p>}
      </div>

      {failed.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg p-3" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)' }}>
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <p className="text-xs text-red-200">Sections that failed: {failed.map(f => f.name).join(', ')}. Everything else is still real data.</p>
        </div>
      )}

      <Section title="1 TAKEN VS BLOCKED" sub="Do the gates leave us better or worse off? Blocked figures are hypothetical (no fill, no queue position); realized exits are real paper fills.">
        <TakenVsBlocked s={sec.taken_vs_blocked} horizon={horizon} />
      </Section>
      <Section title="2 GATE IMPACT" sub="For each major gate: what the signals it blocked did afterwards, net of the spread cost proxy.">
        <GateImpact s={sec.gate_impact} />
      </Section>
      <Section title="3 LATENCY AND FRICTION" sub="Decision to submit to ack to fill, and how far fills land from the decision-time quote.">
        <LatencyFriction s={sec.latency_friction} />
      </Section>
      <Section title="4 PARAMETERS AND HEALTH" sub="Valid parameters under the T-250 final-fold rule, fit coverage, and overnight holds (T-248 end-of-day exits are off).">
        <ParamHealth s={sec.params} />
      </Section>

      {resp && (
        <Section title="HOW THIS IS USED">
          <p className="text-xs text-slate-300">{resp.operating_rule}</p>
          <p className="text-[11px] text-slate-500">{resp.definitions}</p>
          <p className="text-[11px] text-slate-500">{resp.not_evidence_of_profit}</p>
        </Section>
      )}
    </div>
  )
}
